import React, { useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

export const CONTEXT_BANNER_ACTION_SUBJECT_ID = 'connectionsBylineContextBanner';

const i18n = defineMessages({
	heading: {
		id: 'byline-connections.menu.context-banner.heading',
		defaultMessage: 'Why this context matters',
		description:
			'Heading of the banner at the top of the connections byline menu, introducing what the Teamwork Graph context is useful for.',
	},
	description: {
		id: 'byline-connections.menu.context-banner.description',
		defaultMessage: 'Rovo will analyze the Teamwork Graph to surface key takeaways',
		description:
			'Body text of the banner at the top of the connections byline menu, explaining that Rovo reads the work connected to this page and summarises the important parts. "Rovo" and "Teamwork Graph" are product names.',
	},
	generateSummary: {
		id: 'byline-connections.menu.context-banner.generate-summary-button',
		defaultMessage: 'Generate context summary',
		description:
			'Label for the button in the connections byline menu banner that opens the Teamwork Graph preview panel, where Rovo summarises the work connected to this page.',
	},
});

const styles = cssMap({
	banner: {
		boxSizing: 'border-box',
		marginInline: token('space.200'),
		marginBlockEnd: token('space.100'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface.sunken'),
	},
});

// `RovoHexIcon` is a logo rather than an `@atlaskit/icon`, so it needs a small adapter to sit in
// Button's icon slot. Declared at module scope so the reference is stable across renders.
const RovoButtonIcon = () => <RovoHexIcon label="" size="xxsmall" />;

export type ConnectionsByLineContextBannerProps = {
	/**
	 * Opens the Teamwork Graph preview panel. The panel runs `useAtAGlance` on mount, so opening it
	 * is what "generates" the summary — this banner fetches nothing itself.
	 */
	onGenerateSummary: () => void;
};

/**
 * The "Why this context matters" banner pinned under the byline menu header.
 *
 * Hidden entirely when Rovo is unavailable: the copy names Rovo and the action leads to a
 * Rovo-generated summary, so there is nothing to offer without it.
 */
export const ConnectionsByLineContextBanner = ({
	onGenerateSummary,
}: ConnectionsByLineContextBannerProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isRovoLLMEnabled } = useSessionData();
	const hasFiredViewedRef = useRef(false);

	useEffect(() => {
		if (hasFiredViewedRef.current || !isRovoLLMEnabled) {
			return;
		}
		hasFiredViewedRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'banner',
				actionSubjectId: CONTEXT_BANNER_ACTION_SUBJECT_ID,
				source: 'connectionsBylineMenu',
			},
		}).fire();
	}, [createAnalyticsEvent, isRovoLLMEnabled]);

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: CONTEXT_BANNER_ACTION_SUBJECT_ID,
				source: 'connectionsBylineMenu',
			},
		}).fire();
		onGenerateSummary();
	}, [createAnalyticsEvent, onGenerateSummary]);

	if (!isRovoLLMEnabled) {
		return null;
	}

	return (
		<Box xcss={styles.banner} testId="connections-byline-menu.context-banner">
			<Stack space="space.100" alignInline="start">
				<Heading size="xsmall">{formatMessage(i18n.heading)}</Heading>
				<Text color="color.text.subtle" size="small">
					{formatMessage(i18n.description)}
				</Text>
				<Button
					appearance="default"
					iconBefore={RovoButtonIcon}
					shouldFitContainer
					onClick={handleClick}
					testId="connections-byline-menu.generate-context-summary-button"
				>
					{formatMessage(i18n.generateSummary)}
				</Button>
			</Stack>
		</Box>
	);
};
